/** Shared schema.org JSON-LD for Organization, past Events, and confirmed speakers. */

import { getConfirmedSpeakers, type Speaker } from "../data/sharedSpeakers";

export const SITE_ORIGIN = "https://www.waveofinnovation.com";

export const SITE_ORGANIZATION_DESCRIPTION =
  "Wave of Innovation is an independent non-profit events and media organisation that runs XRP Ledger events across the UK and APAC.";

export type JsonLd = Record<string, unknown>;

export function buildOrganizationJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Wave of Innovation",
    alternateName: "WOI",
    url: `${SITE_ORIGIN}/`,
    logo: `${SITE_ORIGIN}/blog/graph_tag.png`,
    description: SITE_ORGANIZATION_DESCRIPTION,
    sameAs: [
      "https://www.linkedin.com/company/wave-of-innovation",
      "https://x.com/wave_of_innov",
      "https://www.facebook.com/waveofinnovationHQ",
      "https://www.instagram.com/waveofinnovation/",
      "https://www.threads.com/@waveofinnovation",
    ],
    knowsAbout: [
      "XRP Ledger",
      "XRPL",
      "digital assets",
      "blockchain",
      "institutional finance",
      "fintech",
    ],
    areaServed: ["United Kingdom", "Australia", "Asia-Pacific"],
  };
}

function sameAsLinks(speaker: Speaker): string[] {
  return [
    speaker.linkedin,
    speaker.twitter,
    speaker.youtube,
    speaker.instagram,
    speaker.website,
    speaker.github,
  ].filter((url): url is string => Boolean(url && url !== "#"));
}

function worksForFromSpeaker(speaker: Speaker): JsonLd | undefined {
  const website =
    speaker.website && speaker.website !== "#" ? speaker.website : undefined;
  const title = speaker.title || "";
  const commaIdx = title.lastIndexOf(",");
  if (commaIdx > 0) {
    const orgName = title.slice(commaIdx + 1).trim();
    if (orgName) {
      const org: JsonLd = { "@type": "Organization", name: orgName };
      if (website) org.url = website;
      return org;
    }
  }
  return undefined;
}

/** Person JSON-LD from confirmed speakers only (map — no hand-written blocks). */
export function buildConfirmedPersonJsonLdList(): JsonLd[] {
  return getConfirmedSpeakers().map((speaker) => {
    const data: JsonLd = {
      "@context": "https://schema.org",
      "@type": "Person",
      name: speaker.name,
      jobTitle: speaker.title,
      description: speaker.description,
    };
    const worksFor = worksForFromSpeaker(speaker);
    if (worksFor) data.worksFor = worksFor;
    const sameAs = sameAsLinks(speaker);
    if (sameAs.length) data.sameAs = sameAs;
    return data;
  });
}

function buildSydneyPerformers(): JsonLd[] {
  return getConfirmedSpeakers()
    .filter((s) => (s.events ?? ["xrp-sydney-2026"]).includes("xrp-sydney-2026"))
    .map((speaker) => ({
      "@type": "Person",
      name: speaker.name,
      jobTitle: speaker.title,
    }));
}

/** Past delivered events — track record proof for AI answer engines. */
export function buildPastEventJsonLdList(): JsonLd[] {
  return [
    {
      "@context": "https://schema.org",
      "@type": "Event",
      name: "XRP Gold Coast 2024",
      eventStatus: "https://schema.org/EventCompleted",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      startDate: "2024-03",
      location: {
        "@type": "Place",
        name: "Royal Pines Resort",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Gold Coast",
          addressRegion: "QLD",
          addressCountry: "AU",
        },
      },
      organizer: {
        "@type": "Organization",
        name: "Wave of Innovation",
        url: `${SITE_ORIGIN}/`,
      },
      description:
        "XRP Gold Coast 2024 brought together 300 attendees and 20 sponsors, with a global audience travelling in from the UK, US, Singapore and Hong Kong. Ripple sponsored and John Deaton headlined.",
      sponsor: { "@type": "Organization", name: "Ripple" },
    },
    {
      "@context": "https://schema.org",
      "@type": "Event",
      name: "XRP Australia 2026",
      eventStatus: "https://schema.org/EventCompleted",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      startDate: "2026-02-27",
      endDate: "2026-02-27",
      url: `${SITE_ORIGIN}/sydney2026`,
      location: {
        "@type": "Place",
        name: "Crown Towers Sydney",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1 Barangaroo Avenue",
          addressLocality: "Sydney",
          addressRegion: "NSW",
          addressCountry: "AU",
        },
      },
      organizer: {
        "@type": "Organization",
        name: "Wave of Innovation",
        url: `${SITE_ORIGIN}/`,
      },
      description:
        "XRP Australia 2026, held at Crown Towers Sydney, hosted 400 attendees and 11 sponsors, with Ripple CEO Brad Garlinghouse, Monica Long and David Schwartz on stage. The partnership built in Sydney became the confirmed Title Sponsorship for the next London event.",
      sponsor: { "@type": "Organization", name: "Ripple" },
      performer: buildSydneyPerformers(),
    },
    // TODO: 2027 events — when London/Sydney 2027 are announceable, add Event blocks with
    // eventStatus EventScheduled, real startDate, venue, offers (ticket URL), and performer[].
  ];
}

/** Escape so JSON cannot break out of a script tag. */
export function jsonLdScriptContent(data: JsonLd | JsonLd[]): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}
